import { compareAsc } from 'date-fns'
import { useCallback, useMemo, useState } from 'react'
import { type CascadingMenuOption } from '@opallabs/concepts'
import { sortResourcesByDateRelevancy } from '@opallabs/lambda'
import { DeprecatedSingleSelectOption, Icon } from '@opallabs/lapidary'
import { useAddToPlanData } from './queries'
import type {
  AddToPlanModalProps,
  MomentData,
  Plan,
  UseAddToPlanStateValue,
} from './types'

type UseAddToPlanStateArgs = Pick<
  AddToPlanModalProps,
  'onAddToPlan' | 'onCloseModal'
> & {
  moments: MomentData[]
}

export const useAddToPlanState = ({
  moments,
  onAddToPlan,
  onCloseModal,
}: UseAddToPlanStateArgs): UseAddToPlanStateValue => {
  const [selectedPlanItem, setSelectedPlan] =
    useState<CascadingMenuOption | null>(null)
  const [selectedCategoryType, setSelectedCategoryType] =
    useState<DeprecatedSingleSelectOption | null>(null)
  const [selectedSwimlane, setSelectedSwimlane] =
    useState<CascadingMenuOption | null>(null)

  const updateDisabled = !selectedPlanItem || !selectedCategoryType

  const {
    categoryTypeOptions,
    isInitialLoading,
    isOnPlan,
    isSwimlanesLoading,
    planTitle,
    plans,
    swimlaneOptions,
  } = useAddToPlanData({
    moments,
    selectedPlanItem,
    setSelectedCategoryType,
  })

  const validMoments = useMemo(
    () =>
      moments
        .filter(({ isStamp }) => !isStamp)
        .sort((a, b) => {
          if (a.schedule?.startDate && b.schedule?.startDate) {
            return compareAsc(a.schedule.startDate, b.schedule.startDate)
          }

          if (a.schedule?.startDate && !b.schedule?.startDate) {
            return 1
          }

          return a.title.localeCompare(b.title)
        }),

    [moments]
  )
  const momentStamps = useMemo(
    () => moments.filter(({ isStamp }) => isStamp),
    [moments]
  )
  const planItemOptions = useMemo<CascadingMenuOption[]>(() => {
    if (!plans) return [] as CascadingMenuOption[]

    // Get moment schedule dates for relevancy sorting
    const momentStartDate = validMoments[0]?.schedule?.startDate
      ? new Date(validMoments[0].schedule.startDate)
      : null
    const lastMomentEndDate = validMoments
      .toReversed()
      .find((moment) => moment.schedule?.endDate)
    const momentEndDate = lastMomentEndDate?.schedule?.endDate
      ? new Date(lastMomentEndDate.schedule.endDate)
      : null

    // Sort plans by relevancy using utility function
    const sortedPlans = sortResourcesByDateRelevancy<Plan>(
      plans,
      momentStartDate,
      momentEndDate
    )

    return builPlanTree(sortedPlans)
  }, [plans, validMoments])

  const onSetPlan = useCallback((planItem: CascadingMenuOption | null) => {
    // Reset swimlane selection when plan changes
    setSelectedSwimlane(null)

    setSelectedPlan(planItem)
  }, [])

  const addToPlan = useCallback(() => {
    if (updateDisabled || !validMoments || !plans) {
      return
    }
    const selectedPlan = plans.find(({ id }) => id === selectedPlanItem.value)

    if (!selectedPlan) return

    onAddToPlan({
      moments: validMoments,
      invalidMomentCount: momentStamps.length,
      selectedPlan,
      selectedSwimlane,
      selectedCategoryType,
    })
    onCloseModal(selectedPlanItem.value)
  }, [
    onAddToPlan,
    onCloseModal,
    momentStamps,
    plans,
    selectedPlanItem,
    selectedSwimlane,
    selectedCategoryType,
    updateDisabled,
    validMoments,
  ])

  const actions = useMemo(
    () => ({
      addToPlan,
      setSelectedPlanItem: onSetPlan,
      setSelectedCategoryType,
      setSelectedSwimlane,
    }),
    [addToPlan, onSetPlan, setSelectedCategoryType, setSelectedSwimlane]
  )

  const availableOptions = useMemo(
    () => ({
      categoryTypeOptions,
      planItemOptions,
      swimlaneOptions,
    }),
    [categoryTypeOptions, planItemOptions, swimlaneOptions]
  )

  const selectedOptions = useMemo(
    () => ({
      selectedCategoryType,
      selectedPlanItem,
      selectedSwimlane,
    }),
    [selectedCategoryType, selectedPlanItem, selectedSwimlane]
  )

  const loadingStates = useMemo(
    () => ({
      isInitialLoading: !moments.length || isInitialLoading,
      isSwimlanesLoading,
    }),
    [isInitialLoading, isSwimlanesLoading, moments]
  )

  const validation = useMemo(
    () => ({
      isOnPlan: Boolean(isOnPlan),
      planTitle,
      momentStamps,
      updateDisabled,
    }),
    [isOnPlan, planTitle, momentStamps, updateDisabled]
  )

  return {
    actions,
    availableOptions,
    selectedOptions,
    loadingStates,
    validation,
  }
}

const findChildPlans = (plans: Plan[], parentPlanId: string) => {
  return plans.reduce<CascadingMenuOption[]>((accum, plan) => {
    if (plan.parentBlock?.parentPlan?.id === parentPlanId) {
      const planItem: CascadingMenuOption = {
        value: plan.id,
        label: plan.title,
        leadingNode: <Icon glyph="gem-plans" />,
        childOptions: findChildPlans(plans, plan.id),
      }

      return [...accum, planItem]
    }

    return accum
  }, [])
}

function builPlanTree(plans: Plan[]) {
  return plans.reduce<CascadingMenuOption[]>((accum, plan) => {
    if (plan.parentBlock?.parentPlan?.id) return accum

    const planItem = {
      value: plan.id,
      label: plan.title,
      leadingNode: <Icon glyph="gem-plans" />,
      childOptions: findChildPlans(plans, plan.id),
    }

    return [...accum, planItem]
  }, [])
}
